// Every user-facing string in the app, in one flat object.
//
// Two reasons it lives here even though there is exactly one language
// (SUR-18): a caregiver can review the whole vocabulary in one place,
// and INV-05 — no user-facing English, including thrown errors and
// status text — is checkable by reading one file.
//
// Swiss orthography throughout: `ss`, never `ß`. The keyboard has no `ß` key, so
// a string containing one would be untypeable text shown to someone who has
// written `ss` all their life. A unit test asserts this.

export const de = {
  appName: "Sag's",
  appTagline: 'Gedanken werden Worte',

  // Surfaces
  surfaceSprechen: 'Sprechen',
  surfaceSaetze: 'Sätze',
  surfaceNotfall: 'Notfall',
  surfacePartnerAnsicht: 'Partner-Ansicht',
  surfaceBetreuerModus: 'Betreuer-Modus',

  // Core actions
  speak: 'Sprechen',
  stop: 'Stopp',
  deleteChar: 'Löschen',
  deleteWord: 'Wort löschen',
  space: 'Leerzeichen',
  back: 'Zurück',
  cancel: 'Abbrechen',
  confirm: 'Bestätigen',
  save: 'Speichern',
  rotate: 'Drehen',

  // Partner-Ansicht. Shown when the buffer is empty: it tells the partner to wait, which is the
  // most useful thing this screen can say (FR19).
  partnerWaiting: 'ich schreibe…',
  // What the output bar is: a tap on it shows the sentence full-screen for the partner.
  showText: 'Text anzeigen',

  // The repair row (3.8). The labels are what the person reads on the four keys; the texts are what is
  // spoken if the seeded tile behind a key has been deleted, which is why both live here — a fallback
  // that reads differently from the tile it replaces is a second vocabulary nobody reviewed.
  repairNotThatLabel: 'Nicht das',
  repairNotThatText: 'Nein, etwas anderes.',
  repairAgainLabel: 'Wiederhol das',
  repairAgainText: 'Bitte wiederhol das.',
  repairWaitLabel: 'Ich schreibe',
  repairWaitText: 'Bitte warte, ich schreibe.',
  repairYesLabel: 'Ja, genau',
  repairYesText: 'Ja, genau.',

  // The output bar's two gestures (ADR-043).
  //
  // `Tastatur` names the destination, the way ADR-010 already chose for the bottom row's `Sätze` slot. Not
  // `Schreiben`: one letter from `Sprechen`, which is the biggest target in the bar and the one action that
  // must never be hit by mistake.
  showKeyboard: 'Tastatur',
  // The offer after a hold cleared the sentence. Short on the tile, whole as the accessible name — the band
  // is 60 % of the width and `Satz zurückholen` at button size would wrap.
  undoClear: 'Zurückholen',
  undoClearName: 'Satz zurückholen',

  // Quick fires are seed data, not strings — see src/data/seed/quickFires.ts

  // Audio
  //
  // `enableAudio` is reused as the middle step of the repair offer (`SUR-23`) rather than replaced by a
  // new sentence. It is already on the device, and until the repair existed it was a promise the code
  // could not keep — a tap that only resumed could not revive a context iOS had closed. The words become
  // true; changing them at the same moment would relabel the one control the person may already know.
  enableAudio: 'Ton aktivieren – antippen',
  // The same sentence with the verb the mode can actually deliver (ADR-047). Since ADR-046 the control is a
  // registered target, so in a timed mode the pointer click belongs to the adapter and `antippen` names the
  // one action that will be discarded.
  enableAudioDwell: 'Ton aktivieren – halten',
  enableAudioScanning: 'Ton aktivieren – drücken',
  audioSilent: 'Kein Ton hörbar – Lautstärke und Stummschaltung prüfen',
  audioMissing: 'Audio fehlt',
  prepareAudio: 'Audio vorbereiten',
  // The last resort, and never the first thing offered. Not «Sprache neu starten»: here "Sprache" reads as
  // *language*, and `Sprechen` is both a surface and the speak button. `Neu starten` is already safe mode's
  // word, so this introduces nothing new to learn.
  audioReload: 'App neu starten',
  audioReloadHint: 'Der getippte Satz bleibt erhalten.',
  // Care mode. In `de.ts` rather than inline, against the panels' current convention, and deliberately:
  // `SUR-19` records the inline German as a debt and C76 is what it cost — a caregiver label with the
  // wrong noun made a feature invisible. New strings landing here shrink that surface rather than grow it.
  careAudioRepair: 'Ton reparieren',

  // The offline voice (2.8). Named for what it does rather than for what it is: nobody outside this repository
  // needs to know the word "Piper".
  offlineVoiceTitle: 'Stimme ohne Netz',
  offlineVoiceIntro:
    'Damit spricht das Gerät auch ohne WLAN mit einer echten Stimme. Einmal etwa 90 MB laden, danach funktioniert es offline.',
  offlineVoiceAccent:
    'Achtung: diese Stimme ist Hochdeutsch, nicht Schweizerdeutsch. Sie klingt hörbar anders als die normale Stimme.',
  offlineVoiceEnable: 'Stimme ohne Netz verwenden',
  offlineVoiceDownload: 'Stimme laden (90 MB)',
  offlineVoiceDownloading: 'Wird geladen',
  offlineVoiceReady: 'Geladen und bereit.',
  offlineVoiceNotLoaded: 'Noch nicht geladen.',
  offlineVoiceRemove: 'Stimme löschen',
  offlineVoiceFailed: 'Laden hat nicht geklappt. Bitte das WLAN prüfen und es erneut versuchen.',
  // The other half of "it did not work", and the reason there are two sentences: when the server answers wrongly
  // — the assets are not uploaded, or the file is not the expected one — the WLAN is fine, and sending someone to
  // restart a working router is worse than saying nothing.
  offlineVoiceFailedServer:
    'Die Stimme lässt sich im Moment nicht laden. Das ist kein WLAN-Problem — bitte technisch prüfen lassen.',
  offlineVoiceFailedDetail: 'Technische Angabe zum Weitergeben:',

  // Low-effort mode (3.7). Named for the relief rather than for the mechanism: nobody wants a button labelled
  // "reduced capability mode". A caregiver's control since ADR-039 — the person-facing `Leichter` / `Normal`
  // pill was withdrawn, and the two labels it used went with it.
  careLowEffort: 'Leichter-Modus',

  // The second keyboard layer (GEO-19). The labels are the glyphs the tester asked for by name — "ich würde
  // auf der Tastatur '123' vorstellen [...] und dann wieder das 'ABC' für zurück" — and they say what the
  // board will be after the tap, not what it is now. The spoken names are separate because "123" read aloud
  // is "einhundertdreiundzwanzig", which is not what the button does.
  layerToDigits: '123',
  layerToLetters: 'ABC',
  layerToDigitsName: 'Zahlen',
  layerToLettersName: 'Buchstaben',

  // Keeping the sentence that was just spoken (AUT-05, ADR-041). One word, because it shares the band's
  // frame with the layer switch and gets one flex unit like it. The spoken name says what is kept, which
  // the button has no room to say — "Behalten" alone would leave a screen reader saying "keep" about
  // nothing in particular.
  keepSentence: 'Behalten',
  keepSentenceName: 'Satz behalten',

  // Speech failure
  speechFailedVisualOnly: 'Kein Ton möglich – Text bitte lesen',
  /**
   * Offered when a dwell or scan activation could not be spoken *because* it was not a real tap (3.2).
   * A tap here is a gesture, so the platform voice becomes usable for that one sentence.
   */
  tapToSpeak: 'Antippen zum Sprechen',
  /**
   * The same offer, named by what the person's own mode does (ADR-047).
   *
   * In dwell the control is held like every other target; in scanning the switch is pressed once the
   * highlight arrives. Only these two vary, because only these two discard the tap.
   */
  tapToSpeakDwell: 'Halten zum Sprechen',
  tapToSpeakScanning: 'Drücken zum Sprechen',

  // Zielübung (calibration)
  calibration: 'Zielübung',
  stopExercise: 'Übung beenden',
  targetHere: 'Ziel antippen',
  targetInstruction: 'Bitte den Punkt antippen, sobald Sie ihn sehen.',
  calibrationProposal: 'Vorschlag',
  calibrationProvisional: 'Vorläufig — die Übung wurde früher beendet.',
  calibrationAccept: 'Übernehmen',
  calibrationReject: 'Nicht übernehmen',
  calibrationNoChange: 'Keine Einstellung wird geändert.',
  calibrationStart: 'Zielübung starten',

  // Care mode sections
  careSectionPhrases: 'Sätze und Kategorien',
  careSectionAbbrevs: 'Abkürzungen',
  careSectionSettings: 'Einstellungen',
  careSectionAudio: 'Ton und Stimme',
  careSectionBackup: 'Backup',
  careSectionDiagnostics: 'Diagnose',
  careSectionLearning: 'Gelernt',
  careExit: 'Betreuer-Modus verlassen',
  carePinPrompt: 'Noch keine PIN eingerichtet. Löschen ist nur durch eine Rückfrage geschützt.',
  carePinSet: 'PIN einrichten',
  carePinChange: 'PIN ändern',
  confirmDelete: 'Wirklich löschen?',
  addPhrase: 'Satz hinzufügen',
  addAbbrev: 'Abkürzung hinzufügen',
  delete: 'Löschen',

  // Reordering (AUT-02). Two buttons rather than a drag: a gesture a tired hand can get wrong would
  // rewrite the board silently, and these say what they do.
  moveUp: 'Nach oben',
  moveDown: 'Nach unten',
  pinnedNote: '(angepinnt)',

  // Editing a phrase (AUT-04). The short title is what the tile says; the sentence is what gets
  // spoken, which is the difference between a board of readable tiles and a board of paragraphs.
  edit: 'Ändern',
  phraseShortLabel: 'Kurzer Titel',
  phrasePinned: 'Angepinnt',

  // Categories (AUT-01). Only an empty one can be deleted, and the reason is on the button rather
  // than left for a caregiver to work out.
  addCategory: 'Kategorie hinzufügen',
  categoryName: 'Name der Kategorie',
  categoryIcon: 'Zeichen',
  categoryNotEmpty: 'nicht leer',

  // Abbreviations a caregiver writes (AUT-09). The third one is the important one: an expansion fires
  // on a word boundary, so a code that opens a real word turns that word into somebody else's
  // sentence at the next space.
  abbrevEmpty: 'Bitte einen Code und einen Satz eintragen.',
  abbrevNotShorter: 'Der Code muss kürzer sein als der Satz, für den er steht.',
  abbrevDuplicate:
    'Diesen Code gibt es schon. Bitte zuerst den alten löschen oder einen anderen Code wählen.',
  abbrevPrefixOfWord:
    'Dieser Code beginnt ein echtes Wort. Beim Tippen würde daraus ungewollt ein ganzer Satz — bitte einen anderen Code wählen.',

  // The grid is full (AUT-08). `{name}` and `{max}` are filled by `fill()` below: a caregiver told
  // only "voll" has to work out which of seven categories that was, and the number is the answer to
  // the question they ask next.
  categoryFull:
    'Die Kategorie «{name}» ist voll — sie zeigt höchstens {max} Sätze. Bitte einen Satz löschen oder eine neue Kategorie anlegen.',
  noRoomForCategory:
    'Kein Platz für eine weitere Kategorie — die Sätze-Seite zeigt höchstens {max}. Bitte zuerst eine leere Kategorie löschen.',
  noRoomForPhraseAnywhere:
    'Keine Kategorie hat noch Platz. Bitte zuerst einen Satz löschen oder eine neue Kategorie anlegen.',
  // Where a mined sentence should live. A caregiver picks, because a tile nobody can find is a tile
  // nobody kept.
  candidateCategory: 'Kategorie für diesen Satz',
  inputModeUnavailable: 'ab Phase 3',
  noPin: 'keine',

  // Partner-assisted scanning
  partnerAssistedTitle: 'Buchstaben abfragen',
  partnerAssistedGroupQuestion: 'Ist der Buchstabe in dieser Gruppe?',
  partnerAssistedLetterQuestion: 'Ist es dieser Buchstabe?',
  yes: 'Ja',
  no: 'Nein',
  partnerAssistedNextGroup: 'Nächste Gruppe',

  saveAsPhrase: 'Als Satz speichern?',
  installUpdate: 'Update installieren',
  updateAvailable: 'Ein Update ist bereit',
  resortTiles: 'Neu sortieren',
  exportBackup: 'Backup exportieren',
  importBackup: 'Backup einlesen',
  pinRequired: 'PIN eingeben',
  pinWrong: 'PIN falsch',

  // Storage
  storageNotPersistent:
    'Der Speicher ist nicht dauerhaft geschützt. Bitte regelmässig ein Backup exportieren.',
  storageImportSchemaNewer:
    "Dieses Backup stammt aus einer neueren Version von Sag's und kann hier nicht eingelesen werden. Auf dem Gerät wurde nichts verändert.",
  storageImportInvalid:
    "Diese Datei ist kein Sag's-Backup. Auf dem Gerät wurde nichts verändert.",
  // The file *is* ours and cannot be written. One message for every content failure: a caregiver's next
  // move is the same whichever array is wrong, and the sentence they most need is the one this app could
  // not say before — the device is exactly as it was.
  storageImportIncomplete:
    'Diese Datei ist unvollständig und wurde nicht eingelesen. Auf dem Gerät wurde nichts verändert — die bisherigen Sätze, Wörter und Einstellungen sind alle noch da. Bitte ein Backup verwenden, das das Gerät selbst geschrieben hat.',
  // Validation passed and the write failed anyway. The clear and the writes are one transaction, so
  // "nichts verändert" is a fact rather than a hope.
  storageImportFailed:
    'Das Einlesen hat nicht geklappt. Auf dem Gerät wurde nichts verändert — die bisherigen Daten sind unverändert vorhanden. Bitte es später noch einmal versuchen.',

  // What the three secrets are (`BAK-18`, C86).
  //
  // Three unrelated things a caregiver has to tell apart under stress, and until now the whole explanation was
  // two parenthesised phrases on a screen shown once. Permanent, above the restore controls, and in `de.ts`
  // rather than inline: the caregiver panels carry their German inline as a debt (`SUR-19`) and C76 is what
  // that cost — a label with the wrong noun made a feature invisible. This is prose about the only
  // unrecoverable secret in the app, so it goes where a caregiver can review the whole vocabulary at once.
  secretsExplainerTitle: 'Drei verschiedene Dinge — was ist was',
  secretsExplainerPin:
    'Die PIN (vier Zahlen) schützt nur diesen Betreuer-Modus vor dem Löschen. Sie hat mit dem Backup nichts zu tun. Wird sie vergessen, ist nichts verloren — sie lässt sich unter «Einstellungen» neu setzen.',
  secretsExplainerWords:
    'Die sechs Sicherheitswörter verschlüsseln das Backup in der Cloud. Nur mit ihnen lässt es sich wieder lesen — von niemandem sonst, auch nicht von uns. Sie werden einmal angezeigt und nirgends gespeichert. Ohne diese Wörter sind alle Cloud-Backups für immer unlesbar. Sie gehören auf Papier ins Notfall-Set.',
  secretsExplainerToken:
    'Der Geräteschlüssel meldet dieses Gerät beim Server an. Er entschlüsselt nichts. Gebraucht wird er auf einem neuen Gerät, damit es die Backups überhaupt findet — die sechs Wörter kommen erst danach.',
  secretsExplainerWhen:
    'Beide werden erst gebraucht, wenn ein Gerät ersetzt werden muss: zuerst der Geräteschlüssel, dann die sechs Wörter. Die Anleitung dazu liegt gedruckt im Notfall-Set.',

  // Cloud backup. The tone here is deliberately unsoftened: a lost passphrase means lost backups, and
  // a caregiver who was told "bitte gut aufbewahren" will not treat the card the way that requires.
  backupSecretsTitle: 'Sicherheitswörter und Geräteschlüssel',
  backupSecretsIntro:
    'Diese Angaben werden nur jetzt angezeigt. Bitte auf Papier schreiben und zum Notfall-Set legen.',
  backupSecretsWarning:
    'Ohne die sechs Wörter lässt sich ein Backup aus der Cloud nicht mehr entschlüsseln — auch nicht von uns. Es gibt keinen zweiten Weg.',
  backupPassphraseLabel: 'Sechs Wörter (zum Entschlüsseln)',
  backupTokenLabel: 'Geräteschlüssel (zum Anmelden)',
  backupSecretsAcknowledge: 'Ich habe beides aufgeschrieben',
  backupSecretsStored: 'Sicherheitswörter sind eingerichtet und wurden bereits angezeigt.',
  backupSecretsCreate: 'Sicherheitswörter erstellen',
  backupWrongPassphrase:
    'Die sechs Wörter passen nicht zu diesem Backup. Bitte die Schreibweise auf dem Zettel prüfen.',
  backupInvalid: "Diese Datei ist kein verschlüsseltes Sag's-Backup.",
  backupEnvelopeNewer:
    "Dieses Backup wurde von einer neueren Version von Sag's verschlüsselt und kann hier nicht gelesen werden.",
  backupEnvelopeOlder:
    'Dieses Backup wurde mit einem älteren Verfahren verschlüsselt. Bitte die Wiederherstellungs-Anleitung verwenden.',
  backupPassphraseRequired: 'Bitte die sechs Wörter eingeben.',

  // Automatic backup status. Read by a caregiver in Betreuer-Modus, never shown to the person — a failed
  // upload is not something they can act on, and their screen is for speaking.
  backupAutoTitle: 'Automatisches Backup',
  backupNever: 'Noch nie gesichert.',
  backupNotConfigured:
    'Nicht eingerichtet. Dazu zuerst die Sicherheitswörter erstellen (siehe unten).',
  backupLastSuccess: 'Zuletzt gesichert:',
  backupLastAttempt: 'Letzter Versuch:',
  backupFailing:
    'Das Backup schlägt seit über einer Woche fehl. Bitte die Verbindung prüfen und einmal von Hand sichern.',
  backupNow: 'Jetzt sichern',
  backupRunning: 'Sichern läuft …',
  backupSucceeded: 'Gesichert.',
  backupFailed: 'Sichern hat nicht geklappt. Es wird beim nächsten Start erneut versucht.',

  // Restore.
  restoreTitle: 'Wiederherstellen',
  restoreFromCloud: 'Aus der Cloud wiederherstellen',
  restoreVersionsLoading: 'Backups werden gesucht …',
  restoreNoVersions: 'In der Cloud liegt noch kein Backup.',
  restorePassphrasePrompt: 'Die sechs Wörter vom Zettel eingeben:',
  restoreConfirm: 'Dieses Backup einlesen',
  restoreWorking: 'Wird eingelesen — das kann einen Moment dauern …',
  restoreDone: 'Eingelesen. Audio wird im Hintergrund neu erzeugt.',
  restoreOffer: 'Dieses Gerät ist neu. Backup wiederherstellen?',
  restoreOfferDismiss: 'Neu einrichten',

  // The replacement-device path: this device has no secrets, but a card in the drawer does.
  adoptTitle: 'Ich habe schon ein Backup',
  adoptIntro:
    'Auf einem neuen Gerät: den Geräteschlüssel vom Zettel eintragen. Danach lässt sich ein Backup aus der Cloud holen.',
  adoptTokenLabel: 'Geräteschlüssel vom Zettel',
  adoptSave: 'Übernehmen',
  adoptDone: 'Übernommen. Jetzt unten «Aus der Cloud wiederherstellen».',
  adoptEmpty: 'Bitte den Geräteschlüssel eintragen.',
  mintCaution:
    'Nur für ein Gerät ohne bestehendes Backup: neue Sicherheitswörter machen alle bisherigen Cloud-Backups unlesbar.',

  // Safe mode — plain, short, and readable under stress
  safeModeTitle: 'Notbetrieb',
  safeModeBody: 'Etwas ist schiefgelaufen. Tippen und sprechen geht weiter.',
  safeModeReload: 'Neu starten',
  bootFailedTitle: "Sag's startet nicht",
  bootFailedBody: 'Bitte die gedruckte Karte benutzen und jemanden holen.',
} as const;

export type StringKey = keyof typeof de;

/** Single accessor so a missing key is a type error rather than `undefined` on screen. */
export const t = (key: StringKey): string => de[key];

/**
 * Fills `{name}`-style holes in the strings above.
 *
 * Kept beside the vocabulary so a message and its holes are read together, and so the strings stay
 * plain values — a caregiver reviewing this file reads sentences, not functions.
 */
export const fill = (template: string, values: Record<string, string | number>): string =>
  template.replace(/\{(\w+)\}/g, (whole, key: string) =>
    key in values ? String(values[key]) : whole,
  );
